import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, ShieldCheck, Sparkles } from 'lucide-react';
import Logo from '../components/Logo';
import { APP_NAME } from '../config/brand';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
// A3: yeni link /mekan-davet#t=<token> (URL parçası sunucuya gitmez); eski /mekan-davet/<token> linkleri çalışmaya devam eder.
const readFragmentToken = () => new URLSearchParams(window.location.hash.slice(1)).get('t') || '';

const VenueInviteRegister = () => {
  const { token: pathToken } = useParams();
  const [token] = useState(() => readFragmentToken() || pathToken || '');
  const navigate = useNavigate();
  const { acceptSession } = useAuth();

  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [invite, setInvite] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Tek kullanımlık tokenı adres çubuğundan ve geçmişten hemen sil.
    if (window.location.hash || pathToken) window.history.replaceState(null, '', '/mekan-davet');
    let alive = true;
    (async () => {
      try {
        const res = await axios.post(`${API}/api/venue-invites/check`, { token });
        if (!alive) return;
        setInvite(res.data);
        if (res.data.email_hint) setEmail(res.data.email_hint);
        if (res.data.venue_name_hint) setName(res.data.venue_name_hint);
        setStatus('ok');
      } catch (e) {
        if (!alive) return;
        setErrorMsg(e.response?.data?.detail || 'Davet doğrulanamadı');
        setStatus('error');
      }
    })();
    return () => { alive = false; };
  }, [token, pathToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Şifre en az 8 karakter olmalı');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/register-with-invite`, {
        token, email: email.trim().toLowerCase(), password, name: name.trim(),
      });
      toast.success('Hesabın oluşturuldu — Meko ile mekân DNA’nı oluşturmaya geçiyorsun');
      acceptSession(data);
      navigate(data?.next || '/venue/profile', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kayıt başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-zinc-500">Davet doğrulanıyor…</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="w-full max-w-md text-center" data-testid="invite-error">
          <div className="mb-6 flex flex-col items-center">
            <Logo size="xl" variant="mark" />
          </div>
          <div className="bg-red-50 border border-red-100 rounded-2xl p-6">
            <h1 className="text-lg font-semibold text-red-900 mb-2">Davet geçerli değil</h1>
            <p className="text-sm text-red-700">{errorMsg}</p>
          </div>
          <Link
            to="/login"
            className="inline-block mt-6 text-sm text-zinc-600 hover:text-zinc-900"
          >
            Giriş sayfasına dön
          </Link>
        </div>
      </div>
    );
  }

  const emailLocked = Boolean(invite?.email_hint);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md" data-testid="invite-register-page">
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo size="xl" variant="mark" />
          <div className="mt-6 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs font-medium">
            <Sparkles size={13} /> Özel davet
          </div>
          <h1 className="mt-4 text-2xl font-serif font-semibold tracking-tight text-zinc-900">
            Mekân hesabını oluştur
          </h1>
          <p className="mt-2 text-sm text-zinc-500 max-w-sm">
            {APP_NAME} ekibi seni davet etti. Hesabını aç, ardından Meko ile mekân DNA’nı oluştur;
            profilin ekibimizin onayından sonra keşifte görünür.
          </p>
        </div>

        {invite?.venue_name_hint && (
          <div className="mb-4 flex items-center gap-2 justify-center text-sm text-zinc-600">
            <ShieldCheck size={16} className="text-emerald-600" />
            Davet edilen mekân: <span className="font-medium text-zinc-900">{invite.venue_name_hint}</span>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm space-y-4"
          data-testid="invite-register-form"
        >
          <div>
            <label className="block text-sm text-zinc-600 mb-1.5">Ad Soyad / Mekân yetkilisi</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Ör: Ayşe Yılmaz"
              className="w-full px-4 py-2.5 border border-zinc-200 rounded-lg bg-white text-sm placeholder-zinc-400 focus:outline-none focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 transition-all"
              data-testid="invite-name-input"
            />
          </div>
          <div>
            <label className="block text-sm text-zinc-600 mb-1.5">E-posta</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={emailLocked}
              className={`w-full px-4 py-2.5 border rounded-lg text-sm transition-all focus:outline-none focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 ${
                emailLocked ? 'bg-zinc-50 border-zinc-200 text-zinc-600' : 'bg-white border-zinc-200'
              }`}
              data-testid="invite-email-input"
            />
            {emailLocked && (
              <p className="mt-1 text-[11px] text-zinc-400 flex items-center gap-1">
                <CheckCircle2 size={11} /> Bu davete atanmış e-posta
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm text-zinc-600 mb-1.5">Şifre</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              placeholder="En az 8 karakter"
              className="w-full px-4 py-2.5 border border-zinc-200 rounded-lg bg-white text-sm placeholder-zinc-400 focus:outline-none focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 transition-all"
              data-testid="invite-password-input"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-2.5 rounded-lg bg-[#1E3A8A] text-white text-sm font-semibold hover:bg-[#e57200] transition-colors disabled:opacity-60"
            data-testid="invite-register-submit"
          >
            {submitting ? 'Hesap oluşturuluyor…' : 'Hesabı oluştur ve devam et'}
          </button>
          <p className="text-[11px] text-zinc-400 text-center">
            Devam ederek {APP_NAME} şartlarını kabul ediyorsun.
          </p>
        </form>

        <div className="mt-6 text-center text-xs text-zinc-500">
          Zaten hesabın var mı?{' '}
          <Link to="/login" className="text-zinc-900 font-medium hover:underline">Giriş yap</Link>
        </div>
      </div>
    </div>
  );
};

export default VenueInviteRegister;
